import React, { useEffect, useState } from "react";

import "./index.css";

const projects = [
  {
    title: "Creche Sara Website",
    description:
      "A responsive static website built with HTML, CSS, and JavaScript for a childcare center.",
    stack: "HTML • CSS • JavaScript",
    demo: "https://sarahlalaoui.github.io/creche-sara/",
    code: "https://github.com/sarahlalaoui/creche-sara",
  },

  {
    title: "Project 2 – React Front-End App",
    description:
      "A front-end application built with React using reusable components and state management with Hooks.",
    stack: "React • JavaScript • CSS",

    demo: "https://sarahlalaoui.github.io/Aeroventil/",
    code: "https://github.com/sarahlalaoui/Aeroventil",
  },
];

function App() {
  const [status, setStatus] = useState("idle");
  // idle | sending | success | error

  const [theme, setTheme] = useState("dark");

  // Load theme from localStorage on first render
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "light" || saved === "dark") {
      setTheme(saved);
    }
  }, []);

  // Apply theme to <html> and persist it
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Fade-in animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = document.querySelectorAll(".fade-in");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  return (
    <div className="app">
      <header className="navbar">
        <div className="logo">Sarah.dev</div>

        <nav>
          <a href="#hero">Home</a>
          <a href="#about">About</a>
          <a href="#skills">Skills</a>
          <a href="#projects">Projects</a>
          <a href="#contact">Contact</a>
        </nav>

        <button className="theme-toggle" onClick={toggleTheme}>
          {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
        </button>
      </header>

      <main>
        {/* HERO */}
        <section id="hero" className="hero fade-in">
          <div className="hero-content">
            <p className="tagline">
              <span id="typing"></span>
            </p>

            <h1>
              Hello, I’m <span>Sarah</span>.
            </h1>

            <p className="hero-text">
              I build clean, responsive, and user-friendly web interfaces with
              React and modern JavaScript.
            </p>

            <div className="hero-buttons">
              <a
                href="/Sarah_Lalaoui_Frontend_CV_Pro.pdf"
                target="_blank"
                rel="noreferrer"
                className="btn primary"
              >
                Download CV
              </a>

              <a href="#contact" className="btn secondary">
                Hire Me
              </a>
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section id="about" className="section fade-in">
          <h2>About</h2>
          <p>
            I’m a Master’s student and a Front-End Developer with experience
            building projects using <strong>HTML, CSS, JavaScript</strong> and{" "}
            <strong>React</strong>. I enjoy turning ideas into clean, elegant,
            and user-friendly interfaces.
          </p>

          <div className="about-grid">
            <div className="about-card">
              <h3>What I do</h3>
              <ul>
                <li>UI implementation from designs (HTML/CSS/JS)</li>
                <li>Single-page applications with React</li>
                <li>Responsive, mobile-first layout</li>
              </ul>
            </div>

            <div className="about-card">
              <h3>What I’m looking for</h3>
              <ul>
                <li>Remote front-end internship or freelance projects</li>
                <li>Collaboration on modern web projects</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SKILLS */}
        <section id="skills" className="section section-alt fade-in">
          <h2>Skills</h2>

          <div className="skills-grid">
            <div className="skill-card">
              <h3>Languages</h3>
              <p>HTML5, CSS3, JavaScript (ES6+)</p>
            </div>

            <div className="skill-card">
              <h3>Frameworks</h3>
              <p>React, JSX, basic Hooks</p>
            </div>

            <div className="skill-card">
              <h3>Tools</h3>
              <p>Git, GitHub, VS Code</p>
            </div>

            <div className="skill-card">
              <h3>Other</h3>
              <p>Responsive design, basic UI, debugging</p>
            </div>
          </div>
        </section>

        {/* PROJECTS */}

        <section id="projects" className="section fade-in">
          <h2>Projects</h2>

          <div className="projects-grid">
            {projects.map((p, i) => (
              <article key={i} className="project-card">
                <h3>{p.title}</h3>
                <p>{p.description}</p>
                <p className="stack">{p.stack}</p>

                <div className="project-links">
                  <a href={p.demo} target="_blank" rel="noreferrer">
                    Live Demo
                  </a>
                  <a href={p.code} target="_blank" rel="noreferrer">
                    Source Code
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="section section-alt fade-in">
          <h2>Contact</h2>
          <p>
            Have a project, an opportunity, or a question? Feel free to reach
            out — I’ll get back to you as soon as possible.
          </p>

          <div className="contact-grid">
            <div>
              <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:lalaouisarah01@gmail.com">
                  lalaouisarah01@gmail.com
                </a>
              </p>

              <p>
                <strong>GitHub:</strong>{" "}
                <a
                  href="https://github.com/sarahlalaoui"
                  target="_blank"
                  rel="noreferrer"
                >
                  github.com/sarahlalaoui
                </a>
              </p>

              <p>
                <strong>LinkedIn:</strong>{" "}
                <a
                  href="https://www.linkedin.com/in/sarahlalaoui/"
                  target="_blank"
                  rel="noreferrer"
                >
                  View my LinkedIn
                </a>
              </p>
            </div>

            <form
              className="contact-form"
              onSubmit={async (e) => {
                e.preventDefault();
                setStatus("sending");

                const form = e.currentTarget;
                const formData = new FormData(form);

                try {
                  const res = await fetch("https://formspree.io/f/mykgdbln", {
                    method: "POST",
                    headers: {
                      Accept: "application/json",
                    },
                    body: formData,
                  });

                  if (res.ok) {
                    setStatus("success");
                    form.reset();
                  } else {
                    setStatus("error");
                  }
                } catch (err) {
                  setStatus("error");
                }
              }}
            >
              <input name="name" type="text" placeholder="Full Name" required />
              <input
                name="email"
                type="email"
                placeholder="Email Address"
                required
              />
              <textarea
                name="message"
                rows="4"
                placeholder="Your Message"
                required
              />

              <button
                type="submit"
                className="btn primary"
                disabled={status === "sending"}
              >
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>

              {status === "success" && (
                <p style={{ marginTop: "10px" }}>
                  ✅ Message sent! I’ll get back to you soon.
                </p>
              )}

              {status === "error" && (
                <p style={{ marginTop: "10px" }}>
                  ❌ Something went wrong. Please try again later.
                </p>
              )}
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <p>© {new Date().getFullYear()} Sarah — Front-End Developer</p>
      </footer>

      {/* Typing effect in hero */}
      <TypingEffect />
    </div>
  );
}

function TypingEffect() {
  useEffect(() => {
    const text = "Front-End Developer";
    let i = 0;
    const el = document.getElementById("typing");
    if (!el) return;

    const timer = setInterval(() => {
      if (i <= text.length) {
        el.textContent = text.slice(0, i);
        i++;
      } else {
        clearInterval(timer);
      }
    }, 80);

    return () => clearInterval(timer);
  }, []);

  return null;
}

export default App;
